// src/theme/Navbar/MobileSidebar/PrimaryMenu/index.tsx
import React from 'react';
import Link from '@docusaurus/Link';
import { useThemeConfig } from '@docusaurus/theme-common';
import { useNavbarMobileSidebar } from '@docusaurus/theme-common/internal';
import NavbarItem from '@theme/NavbarItem';
import { SidebarIcon } from '@site/src/components/icons/sidebar-icons';

type AnyItem = any;

function useItems(): AnyItem[] {
  return useThemeConfig().navbar.items as AnyItem[];
}

function MobileNavLink({ item, className, onClick }: { item: AnyItem; className: string; onClick: () => void }) {
  const icon = item.icon as string | undefined;
  const to = item.to as string | undefined;
  const href = item.href as string | undefined;

  const inner = icon ? (
    <span className="flex items-center gap-2.5">
      <SidebarIcon name={icon} className="h-4 w-4 opacity-50 flex-shrink-0" />
      <span>{item.label}</span>
    </span>
  ) : (
    item.label
  );

  if (to)
    return (
      <Link to={to} className={className} onClick={onClick}>
        {inner}
      </Link>
    );
  return (
    <a href={href} className={className} onClick={onClick}>
      {inner}
    </a>
  );
}

// Left-aligned nav links group
const linkCls =
  'block text-[15px] font-medium text-[var(--color-text)] hover:no-underline hover:text-[var(--color-text)] transition-colors duration-150';

export default function NavbarMobileSidebarPrimaryMenu() {
  const items = useItems();
  const sidebar = useNavbarMobileSidebar();
  const close = () => sidebar.toggle();

  // Split into groups for cleaner visual separation
  const navLinks: AnyItem[] = [];
  const utilLinks: AnyItem[] = []; // Docs, locale
  let newsletterItem: AnyItem | null = null;
  let dashboardItem: AnyItem | null = null;

  items.forEach((item) => {
    if (item.className === 'navbar-dashboard-cta') {
      dashboardItem = item;
      return;
    }
    if (item.className === 'navbar-newsletter-cta') {
      newsletterItem = item;
      return;
    }
    if (item.type && item.type !== 'default') {
      utilLinks.push(item);
      return;
    }
    navLinks.push(item);
  });

  return (
    <div className="flex flex-col gap-6 px-5 py-6">
      {/* Main nav links */}
      <nav className="flex flex-col gap-1">
        {navLinks.map((item, idx) => (
          <MobileNavLink
            key={idx}
            item={item}
            className={linkCls + ' py-2.5 px-3 rounded-lg hover:bg-[var(--color-background-subtle)]'}
            onClick={close}
          />
        ))}
      </nav>

      {/* Divider */}
      {utilLinks.length > 0 && <div className="border-t border-[var(--color-separator)]" />}

      {/* Util links (Docs, locale) */}
      {utilLinks.length > 0 && (
        <nav className="flex flex-col gap-1">
          {utilLinks.map((item, idx) => (
            <NavbarItem
              key={idx}
              {...item}
              mobile
              onClick={close}
              className={linkCls + ' py-2.5 px-3 rounded-lg hover:bg-[var(--color-background-subtle)]'}
            />
          ))}
        </nav>
      )}

      {/* Bottom actions */}
      <div className="flex flex-col gap-3 pt-2">
        {/* Newsletter */}
        {newsletterItem && (
          <MobileNavLink
            item={newsletterItem}
            onClick={close}
            className="flex items-center justify-center w-full px-4 py-2.5 rounded-xl border border-[var(--color-separator)] text-sm font-medium text-[var(--color-text-subtle)] hover:text-[var(--color-text)] hover:border-[var(--color-text-subtle)] hover:no-underline transition-colors duration-150"
          />
        )}

        {/* Dashboard CTA */}
        {dashboardItem && (
          <MobileNavLink
            item={dashboardItem}
            onClick={close}
            className="flex items-center justify-center w-full px-4 py-3 rounded-xl text-sm font-semibold bg-[var(--color-black-action)] text-[var(--color-text-on-primary)] hover:opacity-90 hover:no-underline transition-opacity duration-150"
          />
        )}
      </div>
    </div>
  );
}
